import React, { useContext, useState, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { controlClass } from "../../../utility/ui-util";
import ControlLabel from "../ControlLabel/ControlLabel";
import StateContext from "../../../contexts/state-context";
import "../../../styles/VaultPassword.css";
import ReactMarkdown from "react-markdown";
import { generatePassword } from "../../../processor/state-processor";

const TextInput = (props) => {
  const { setPropertyValue } = useContext(StateContext);
  const variable = props.variable;

  // figure out base input type
  const initialType = variable.isEncrypted
    ? "password"
    : variable.type
    ? variable.type
    : "text";

  const [type, setType] = useState(initialType);
  const [buttonClass, setButtonClass] = useState(
    variable.isEncrypted ? "fa fa-eye" : ""
  );

  // if useDefault is not specified, default to true (matches your code)
  const useDefault = variable.useDefault === undefined ? true : false;

  const [validated, setValidated] = useState(false);
  const [autogenerate, setAutogenerate] = useState(true);

  // precompute regexp / min / max once per variable
  const { regexp, min, max } = useMemo(() => {
    const r =
      variable.pattern && typeof variable.pattern === "string"
        ? variable.pattern.match(/^\/(.*?)\/([gim]*)$/)
          ? new RegExp(
              variable.pattern.match(/^\/(.*?)\/([gim]*)$/)[1],
              variable.pattern.match(/^\/(.*?)\/([gim]*)$/)[2]
            )
          : new RegExp(variable.pattern)
        : null;

    return {
      regexp: r,
      min: variable.min ? variable.min : -1,
      max: variable.max ? variable.max : -1,
    };
  }, [variable.pattern, variable.min, variable.max]);

  const validateValue = (value) => {
    const allErrors = {};
    let v = value;

    // mirror your logic: if not using default and value == currentDefault, treat as ''
    if (!useDefault && v === variable.currentDefaultValue) {
      v = "";
    }

    if (min > 0) {
      if (initialType === "number") {
        if (parseFloat(v) < min) {
          allErrors.invalid = "Value must be greater than " + min + ".";
        }
      } else {
        if (String(v).length < min) {
          allErrors.invalid =
            "Length is shorter than the minimum length " + min + ".";
        }
      }
    }

    if (Object.keys(allErrors).length === 0 && max > 0) {
      if (initialType === "number") {
        if (parseFloat(v) > max) {
          allErrors.invalid = "Value must be less than " + max + ".";
        }
      } else {
        if (String(v).length > max) {
          allErrors.invalid =
            "Length is longer than the maximum length " + max + ".";
        }
      }
    }

    if (Object.keys(allErrors).length === 0 && regexp) {
      if (!String(v).match(regexp)) {
        allErrors.invalid = variable.invalidPatternText;
      }
    }

    return allErrors;
  };

  // initial errors based on current value
  const [errors, setErrors] = useState(
    variable.currentValue !== "" ? validateValue(variable.currentValue) : {}
  );

  /**
   * Auto-generate once when:
   * - autogenerate flag is true
   * - variable is auto
   * - modelDefaultValue === 'auto'
   * - not optional / not read-only / not forceDefault
   * - currentValue is empty
   */
  useEffect(() => {
    const shouldAutoGenerate =
      autogenerate &&
      variable.isAuto &&
      variable.modelDefaultValue === "auto" &&
      !variable.isOptional &&
      !variable.isReadOnly &&
      !variable.forceDefault &&
      variable.currentValue === "";

    if (!shouldAutoGenerate) {
      return;
    }

    const password = generatePassword(
      regexp ? regexp : /.*/,
      min > 0 ? min : 6,
      max > 0 ? max : 12
    );

    setPropertyValue(
      props.group,
      props.advanced,
      props.variableName,
      password,
      props.hostIndex
    );

    // after we auto-generate once, don’t keep doing it
    setAutogenerate(false);
  }, [
    autogenerate,
    variable.isAuto,
    variable.modelDefaultValue,
    variable.isOptional,
    variable.isReadOnly,
    variable.forceDefault,
    variable.currentValue,
    regexp,
    min,
    max,
    setPropertyValue,
    props.group,
    props.advanced,
    props.variableName,
    props.hostIndex,
  ]);

  const handleChange = (value) => {
    const allErrors = validateValue(value);
    setErrors(allErrors);

    if (Object.keys(allErrors).length > 0) {
      setValidated(false);
    } else {
      setValidated(true);
    }

    if (value === "") {
      setAutogenerate(false);
    }

    setPropertyValue(
      props.group,
      props.advanced,
      props.variableName,
      value,
      props.hostIndex
    );
  };

  const toggleMask =
    variable.isEncrypted !== undefined && variable.isEncrypted
      ? (e) => {
          e.preventDefault();
          if (type === "password") {
            setButtonClass("fa fa-eye-slash");
            setType("text");
          } else {
            setButtonClass("fa fa-eye");
            setType("password");
          }
        }
      : null;

  return (
    <Form.Group>
      <ControlLabel
        isOptional={variable.isOptional}
        isObsolete={!!variable.isObsolete}
        isReadOnly={variable.isReadOnly}
        text={variable.label}
      />
      <div className="formWithButton">
        <Form.Control
          placeholder={variable.label}
          className={controlClass(variable, variable.currentValue)}
          type={type}
          value={variable.currentValue}
          onChange={(e) => handleChange(e.target.value)}
          readOnly={variable.isReadOnly}
          plaintext={variable.isReadOnly}
          isInvalid={!!errors.invalid}
        />

        {variable.isAuto && (
          <button
            type="button"
            className="auto"
            title="Auto-generate new password"
            onClick={() =>
              handleChange(
                generatePassword(
                  regexp ? regexp : /.*/,
                  min > 0 ? min : 6,
                  max > 0 ? max : 12
                )
              )
            }
          >
            <i className="fas fa-magic" aria-hidden="true"></i>
          </button>
        )}

        {toggleMask && (
          <button
            type="button"
            className="unmask"
            title="Mask/Unmask password to check content"
            onClick={toggleMask}
          >
            <i className={buttonClass} aria-hidden="true"></i>
          </button>
        )}

        {errors.invalid && (
          <Form.Control.Feedback type="invalid">
            {errors.invalid}
          </Form.Control.Feedback>
        )}
      </div>

      {variable.description && (
        <Form.Text className="text-muted">
          <ReactMarkdown skipHtml={true}>
            {variable.description +
              (variable.isAuto
                ? " *(" +
                  (variable.modelDefaultValue === "auto"
                    ? "Auto-generated - "
                    : "") +
                  "Use the Auto-Generation button to " +
                  (variable.modelDefaultValue === "auto" ? "re" : "") +
                  "generate or alternatively choose your own)*"
                : "")}
          </ReactMarkdown>
        </Form.Text>
      )}

      {variable.isDefaultModified && (
        <Form.Text className="text-danger">
          {
            "** This value has been modified from it's original value of '" +
              variable.stateValue +
              "'" /* matches your original text */
          }
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default TextInput;
